"use client";

import { useMemo, useEffect, useCallback, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useCustomAvatarPage } from '@/layouts/custom-avatar-page/model/use-custom-avatar-page'
import { useIdleTimer } from '@/shared/hooks/use-idle-timer'
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent'
import { useAvatarStore } from '@/shared/store/avatar-store'
import { useVadStore, type VadStore } from '@/shared/store/vad'
import type { KioskIndicatorState, KioskAvatarPageProps, SupportedKioskLanguage, KioskLoadingPhase } from '../types/kiosk-config'
import { KIOSK_LOADING_MESSAGES } from '../types/kiosk-config'
import { toastActions } from '@/layouts/custom-avatar-page/toast/model/toast-store'

interface UseKioskAvatarPageParams extends KioskAvatarPageProps {
  /** 유휴 타임아웃 (밀리초) - 기본 30초 */
  idleTimeoutMs?: number;
  /** 타이머 경고 시작 시점 (남은 밀리초) - 기본 10초 */
  timerWarningMs?: number;
}

export interface UseKioskAvatarPageReturn {
  // 기존 useCustomAvatarPage 리턴 값
  customAvatarPageProps: ReturnType<typeof useCustomAvatarPage>;

  // 키오스크 전용 상태
  indicatorState: KioskIndicatorState;
  bubbleText: string;

  // 유휴 타이머
  isTimerVisible: boolean;
  remainingSeconds: number;

  // 언어 선택기
  selectedLanguage: SupportedKioskLanguage;
  setSelectedLanguage: (lang: SupportedKioskLanguage) => void;

  // 커버 모달
  showCoverModal: boolean;
  isInitializing: boolean;

  // 통합 로딩 상태
  unifiedLoadingState: KioskLoadingPhase;
  loadingMessage: string;

  // 음성 입력 pitch (이퀄라이저 애니메이션용)
  pitch: number;

  // 액션
  handleStart: () => void;
  handleScreenTouch: () => void;
  resetIdleTimer: () => void;
  /** 로고 클릭 시 전체 세션 리셋 (커버 모달로 복귀) */
  handleLogoReset: () => void;
}

/**
 * 키오스크 모드 전용 비즈니스 로직 Hook
 *
 * useCustomAvatarPage를 래핑하여 키오스크 전용 기능 추가:
 * - 자동 재연결 (버튼 클릭 불필요)
 * - 30초 유휴 타이머 (20초 무음 → 10초 카운트다운)
 * - 인디케이터 상태 관리
 * - 언어 선택기 상태
 */
export const useKioskAvatarPage = ({
  agentId,
  idleTimeoutMs = 30000,  // 유휴 30초 후 세션 리셋 (20초 조용한 대기 + 10초 카운트다운)
  timerWarningMs = 10000,  // 마지막 10초에 카운트다운 표시
}: UseKioskAvatarPageParams): UseKioskAvatarPageReturn => {
  // ==================== 기존 CustomAvatarPage 로직 재사용 ====================
  const customAvatarPageProps = useCustomAvatarPage({ agentId, displayMode: 'kiosk' })

  const {
    consentController,
    agentData,
    isAgentLoading,
    showReconnectModal,
    handleReconnect,
    pauseAudio,
    avatarType,
    isCustomAvatarLoaded,
  } = customAvatarPageProps

  // ==================== Store 구독 ====================
  const {
    isLoading,
    hasConsented,
    messages,
    setMessages,
    setSessionId,
  } = useRealtimeAgentStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      hasConsented: state.hasConsented,
      messages: state.messages,
      setMessages: state.setMessages,
      setSessionId: state.setSessionId,
    }))
  )

  const { vadStatus, setVadStatus } = useVadStore(
    useShallow((state: VadStore) => ({
      vadStatus: state.vadStatus,
      setVadStatus: state.setVadStatus,
    }))
  )

  // 3D 아바타 로딩 상태 & 애니메이션 상태
  const { isAvatarReady, currentAnimation, setCurrentAnimation } = useAvatarStore(
    useShallow((state) => ({
      isAvatarReady: state.isAvatarReady,
      currentAnimation: state.currentAnimation,
      setCurrentAnimation: state.setCurrentAnimation,
    }))
  )

  // ==================== 키오스크 전용 상태 ====================
  const [showCoverModal, setShowCoverModal] = useState(true)
  const [isInitializing, setIsInitializing] = useState(true)
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedKioskLanguage>('ko')
  const [hasVadStarted, setHasVadStarted] = useState(false) // VAD listening 시작 여부 (세션 시작 감지용)

  // ==================== 통합 로딩 상태 계산 ====================
  const unifiedLoadingState: KioskLoadingPhase = useMemo(() => {
    // 1. 에이전트 데이터 로딩 중
    if (isAgentLoading || !agentData) {
      return 'loading-agent'
    }

    // 2. 커스텀 아바타 데이터 로딩 중 (custom 타입일 때만)
    if (avatarType === 'custom' && !isCustomAvatarLoaded) {
      return 'loading-avatar'
    }

    // 3. 3D 아바타 렌더링 로딩 중
    if (!isAvatarReady) {
      return 'loading-3d'
    }

    // 4. 모든 데이터 준비 완료
    return 'ready'
  }, [isAgentLoading, agentData, avatarType, isCustomAvatarLoaded, isAvatarReady])

  const loadingMessage = useMemo(() => {
    return KIOSK_LOADING_MESSAGES[unifiedLoadingState]
  }, [unifiedLoadingState])

  // ==================== 인디케이터 상태 계산 ====================
  const indicatorState: KioskIndicatorState = useMemo(() => {
    if (showCoverModal || !hasConsented) return 'idle'
    if (currentAnimation === 'speaking') return 'speaking'
    if (isLoading) return 'loading'
    if (vadStatus === 'listening') return 'listening'
    return 'idle'
  }, [showCoverModal, hasConsented, currentAnimation, isLoading, vadStatus])

  // indicatorState를 ref로 추적 (setInterval 내 stale closure 방지)
  const indicatorStateRef = useRef(indicatorState)
  indicatorStateRef.current = indicatorState
  const prevIndicatorStateRef = useRef(indicatorState) // listening 전환 감지용

  // ==================== Bubble 텍스트 계산 ====================
  const bubbleText = useMemo(() => {
    // 마지막 메시지 가져오기
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null

    if (indicatorState === 'speaking' && lastMessage?.sender === 'agent') {
      return lastMessage.message || ''
    }
    if (indicatorState === 'loading' && lastMessage?.sender === 'user') {
      return lastMessage.message || ''
    }
    if (indicatorState === 'listening') {
      return '안녕하세요 무엇을 도와드릴까요?'
    }
    return ''
  }, [indicatorState, messages])

  // ==================== 재연결 후 키오스크 상태 정리 ====================
  // 실제 재연결은 useCustomAvatarPage 내부에서 단독 처리.
  // 키오스크는 재연결 완료 감지(showReconnectModal: true→false) 시 상태만 정리.
  const wasReconnectingRef = useRef(false)
  useEffect(() => {
    if (showReconnectModal) {
      wasReconnectingRef.current = true
    } else if (wasReconnectingRef.current) {
      // 재연결 완료 (true → false 전환)
      console.log('✅ [KIOSK] 재연결 완료 감지 - 커버 모달로 복귀')
      wasReconnectingRef.current = false
      setShowCoverModal(true)
      setHasVadStarted(false)
      // consentController도 리셋하여 내부 VAD 동기화가 올바르게 동작하도록 보장
      consentController.resetConsent()
    }
  }, [showReconnectModal, consentController])

  // ==================== 세션 리셋 핵심 로직 (타이머 없이) ====================
  const resetSessionCore = useCallback(() => {
    console.log('🔄 [KIOSK] 세션 초기화')

    // 1. 오디오 재생 중지
    pauseAudio()

    // 2. 현재 턴 데이터 클리어
    useRealtimeAgentStore.getState().clearCurrentTurn()

    // 3. 토스트 정리 (TTS 자막 등)
    toastActions.clearAll()

    // 4. 메시지 초기화
    setMessages([])

    // 5. 세션 ID 리셋 (다음 연결 시 새 UUID 생성됨)
    setSessionId(null as unknown as string)

    // 6. VAD 비활성화
    setVadStatus('paused')

    // 7. 애니메이션 초기 상태로
    setCurrentAnimation("toss")

    // 8. Consent 리셋 (handleStart에서 다시 grantConsent 호출됨)
    consentController.resetConsent()

    // 9. VAD 시작 플래그 리셋
    setHasVadStarted(false)

    // 10. 상태 업데이트
    setShowCoverModal(true)
  }, [pauseAudio, setMessages, setSessionId, setVadStatus, setCurrentAnimation, consentController])

  // ==================== 유휴 타이머 Hook (공통) ====================
  const idleTimer = useIdleTimer({
    idleTimeoutMs,
    warningMs: timerWarningMs,
    enabled: !showCoverModal && hasConsented && hasVadStarted,
    isPaused: indicatorState === 'speaking' || indicatorState === 'loading',
    onTimeout: resetSessionCore,
    debugPrefix: 'KIOSK',
  })

  // ==================== 로고 클릭 시 전체 세션 리셋 ====================
  const handleLogoReset = useCallback(() => {
    resetSessionCore()
    idleTimer.stopTimer()
  }, [resetSessionCore, idleTimer])

  // indicatorState 전환 감지: 타이머 시작
  // - listening 진입: VAD 시작 + 타이머 시작
  // - idle 진입 (대화 시작 후): 마이크 뮤트 시에도 타이머 작동
  useEffect(() => {
    const prev = prevIndicatorStateRef.current
    prevIndicatorStateRef.current = indicatorState

    // Case 1: listening 진입 → VAD 시작 플래그 + 타이머 시작
    if (indicatorState === 'listening' && prev !== 'listening' && hasConsented) {
      if (!hasVadStarted) setHasVadStarted(true)
      console.log(`🎤 [KIOSK] listening 진입 (from: ${prev}) - 타이머 시작`)
      idleTimer.startTimer()
    }

    // Case 2: idle 진입 (speaking/loading/listening에서) → 마이크 뮤트 대응
    // 조건: 대화가 이미 시작된 상태 (hasVadStarted=true)
    if (indicatorState === 'idle' && prev !== 'idle' && hasConsented && hasVadStarted) {
      console.log(`⏸️ [KIOSK] idle 진입 (from: ${prev}) - 마이크 뮤트 감지, 타이머 시작`)
      idleTimer.startTimer()
    }
  }, [indicatorState, hasConsented, hasVadStarted, idleTimer])

  // 사용자 메시지(STT) 감지: 타이머 정지
  const prevMessagesLenRef = useRef(messages.length)
  useEffect(() => {
    if (messages.length > prevMessagesLenRef.current) {
      const lastMsg = messages[messages.length - 1]
      if (lastMsg?.sender === 'user') {
        console.log('💬 [KIOSK] 사용자 메시지 감지 - 타이머 정지')
        idleTimer.stopTimer()
      }
    }
    prevMessagesLenRef.current = messages.length
  }, [messages, idleTimer])

  // ==================== 초기화 상태 관리 ====================
  useEffect(() => {
    // 에이전트 데이터 로드 완료 시 초기화 완료
    if (agentData && !isAgentLoading) {
      setIsInitializing(false)
    }
  }, [agentData, isAgentLoading])

  // ==================== 액션 핸들러 ====================
  const handleStart = useCallback(() => {
    // 아바타가 준비되지 않았으면 진입 불가
    if (!isAvatarReady) {
      console.log('🚫 [KIOSK] 아바타 미준비 - 진입 불가')
      return
    }

    console.log('🚀 [KIOSK] 시작 버튼 클릭 - 아바타 준비 완료')
    setShowCoverModal(false)
    consentController.grantConsent()

    // 🔧 핵심 수정: 세션 시작 시 즉시 VAD 시작 플래그 + 타이머 설정
    // - hasVadStarted=true → useIdleTimer의 enabled 조건 충족
    // - idleTimer.startTimer() → 마이크 ON/OFF 상관없이 타이머 즉시 작동
    setHasVadStarted(true)
    idleTimer.startTimer()
    console.log('⏱️ [KIOSK] 세션 시작 - 유휴 타이머 활성화')
  }, [consentController, idleTimer, isAvatarReady])

  const handleScreenTouch = useCallback(() => {
    if (indicatorState === 'speaking') {
      console.log('👆 [KIOSK] 화면 터치 - interrupt (오디오 중지 → listening 상태로 전환)')

      // 1. 오디오 중지 및 청크 클리어
      pauseAudio()

      // 2. 현재 턴 데이터 클리어
      useRealtimeAgentStore.getState().clearCurrentTurn()

      // 3. isLoading 초기화 (loading 상태 잔류 방지 → indicatorState가 즉시 listening 전환)
      useRealtimeAgentStore.getState().setLoading(false)

      // 4. 토스트 정리 (TTS 자막 등)
      toastActions.clearAll()

      // 5. VAD를 listening 상태로 전환
      setVadStatus('listening')

      // 6. 애니메이션을 listening으로 변경
      setCurrentAnimation('listening')
    }
  }, [indicatorState, pauseAudio, setVadStatus, setCurrentAnimation])

  return {
    customAvatarPageProps,
    indicatorState,
    bubbleText,
    isTimerVisible: idleTimer.isTimerVisible,
    remainingSeconds: idleTimer.remainingSeconds,
    selectedLanguage,
    setSelectedLanguage,
    showCoverModal,
    isInitializing,
    unifiedLoadingState,
    loadingMessage,
    pitch: customAvatarPageProps.pitch,
    handleStart,
    handleScreenTouch,
    resetIdleTimer: idleTimer.resetTimer,
    handleLogoReset,
  }
}
